/**
 * THE APP SHELL: header, three views, bottom nav.
 *
 * The `Jobs` view was a placeholder while `src/forms/**` and `src/capture/**` were being written
 * concurrently. **Both have landed and it is now wired**, at the mount point their author marked, to
 * `WorkFlow` from `src/compose/` — the composition layer, which is the only module that imports all
 * three subsystems. This file still imports nothing from `src/forms/` or `src/capture/` directly.
 *
 * (`src/compose/`, not `src/app/`: on a case-insensitive filesystem a `src/app/` directory and this
 * package's `src/App.tsx` are the same path, and tsc refuses the program with TS1149.)
 *
 * Hash routing, not the History API. An installed PWA launched from the Home Screen and then killed
 * reopens at `start_url`, and a technician's back gesture inside a standalone window behaves less
 * surprisingly with hashes. It also means no server-side rewrite is needed for a deep link, which
 * matters because CloudFront's SPA fallback already rewrites 403/404 to `index.html` and stacking two
 * rewrite mechanisms is how deep links start 404ing in one environment only.
 *
 * ⚠️ **SIGN-IN IS NOW ON SCREEN.** `src/auth/` was built, tested and installed at startup while this
 * file was left untouched to avoid a collision, with the result that `SignInBanner` existed and was
 * mounted nowhere: a technician had no sign-in affordance and no session feedback at all. It is mounted
 * below, through {@link useAuthPhase}.
 *
 * **EXACTLY ONE SIGN-IN SURFACE IS RENDERED, EVER**, and the choice is made on the token provider's
 * `kind` rather than on a phase:
 *
 *  · `stub` → {@link AuthStubBanner}. This build has no sign-in configuration, and that banner is
 *    already this app's honest words for it — including the actual blocker (the Federate service
 *    profile, OQ-5), which no phase `detail` can name. `install.ts`'s own header rejects the
 *    alternative: *"a second banner saying the same thing"*.
 *  · anything else → {@link SignInBanner}, driven by the provider's live phase.
 *
 * The standing state is on the **Device** screen, not here — see `SignInBanner`'s own comment on why a
 * permanent green "Signed in" banner above every job is a banner nobody reads. `DevicePanel` below is
 * therefore where `signed-in` and `not-configured` are stated in words, and it is the only place the
 * detail always shows (the same rule it already follows for the four-way storage verdict).
 *
 * **These strings are hardcoded English on purpose, for the Phase 1 pilot** — a decision, not an
 * oversight. See `ENGLISH_ONLY_PILOT_DECISION` in `src/i18n/index.ts` for what was decided, why
 * (PRD Phase 1 says English only), and what would reopen it. This file remains the natural home for
 * the locale picker and the `Translator` context when that decision reopens.
 */

import { useEffect, useRef, useState } from 'react';
import { describeAuthPhase, SignInBanner } from '../auth';
import { WorkFlow } from '../compose';
import type { TokenProvider } from '../core/auth';
import { AuthStubBanner, OfflineBanner, StorageBanner } from './Banners';
import { OutboxPanel } from './OutboxPanel';
import './forms.css';
import { useAuthPhase, type AuthBinding } from './useAuthPhase';
import { useOnline, useOutbox, useStorageAssessment } from './useOutbox';

type View = 'jobs' | 'outbox' | 'settings';

const VIEWS: readonly { id: View; label: string }[] = [
  { id: 'jobs', label: 'Jobs' },
  { id: 'outbox', label: 'Outbox' },
  { id: 'settings', label: 'Device' },
];

function viewFromHash(): View {
  const raw = globalThis.location?.hash.replace(/^#\/?/, '') ?? '';
  // `jobs/...` is the WorkFlow's position within the jobs view (D4, see flow.ts) — any
  // member of that family is still the jobs view to this router.
  if (raw === 'jobs' || raw.startsWith('jobs/')) {
    return 'jobs';
  }
  return VIEWS.some((view) => view.id === raw) ? (raw as View) : 'jobs';
}

export function AppShell(): JSX.Element {
  const [view, setView] = useState<View>(viewFromHash);
  const { items, counts } = useOutbox();
  const online = useOnline();
  const storage = useStorageAssessment();
  const auth = useAuthPhase();
  /**
   * The flow's current hash path, reported by `WorkFlow` (D4). Held in a REF, not state: it
   * is read only inside `go('jobs')`, and re-rendering the shell on every flow step for a
   * value no render reads would be work for nothing.
   */
  const flowPath = useRef('jobs');

  useEffect(() => {
    const onHashChange = (): void => setView(viewFromHash());
    globalThis.addEventListener('hashchange', onHashChange);
    return () => globalThis.removeEventListener('hashchange', onHashChange);
  }, []);

  const go = (next: View): void => {
    /*
     * The Jobs tab restores the FLOW'S OWN position rather than writing a bare `#/jobs`.
     * Not cosmetic: WorkFlow's hash listener reads a jobs-family hash that disagrees with
     * its screen as a back gesture (that is what a back gesture looks like), so clobbering
     * the hash here would make the Jobs tab step a mid-form technician backwards.
     */
    globalThis.location.hash = next === 'jobs' ? `#/${flowPath.current}` : `#/${next}`;
    setView(next);
  };

  // Pending work that is NOT yet sent. `synced` rows are excluded: a badge that counts finished work
  // is a badge that never clears, and a badge that never clears is ignored.
  const pending = counts.queued + counts.inFlight;

  return (
    <div className="app">
      <header className="app__header">
        <h1 className="app__title">Locker FieldKit</h1>
      </header>

      <main className="app__main">
        <OfflineBanner online={online} queued={counts.queued} />
        <StorageBanner assessment={storage} />
        {/*
          One surface, chosen on the provider — see the file header. `onSignIn` is spread rather than
          passed as `undefined`: `exactOptionalPropertyTypes` treats an explicit `undefined` as a
          different type from an absent key, and more importantly a bound handler that exists but can
          never work is how a dead button gets shipped.

          The BUTTON is not this file's decision. `describeAuthPhase(...).canStartSignIn` is `false` for
          a first sign-in while offline — the sign-in page and the passkey check are both network — and
          `SignInBanner` renders no button unless it is `true`. Passing `online` is what carries that.
        */}
        {auth.provider.kind === 'stub' ? (
          <AuthStubBanner provider={auth.provider} />
        ) : (
          <SignInBanner
            phase={auth.phase}
            online={online}
            {...(auth.signIn !== undefined ? { onSignIn: auth.signIn } : {})}
          />
        )}

        {counts.failed > 0 && view !== 'outbox' ? (
          <div className="banner banner--danger" role="alert">
            <p className="banner__title">
              {counts.failed} {counts.failed === 1 ? 'submission' : 'submissions'} could not be sent
            </p>
            {/*
              An error has to say what to do next, not only what happened. "Open the Outbox to see why"
              named the place; it did not say that the work is still on the phone (so nothing is lost
              yet), nor that the technician is the only thing that will move it.
            */}
            <p className="banner__body">
              The work is still saved on this phone and nothing has been lost — but these will not retry
              on their own. Open <strong>Outbox</strong> below, read the reason on each one, and press{' '}
              <strong>Try again</strong>.
            </p>
            <p>
              <button type="button" className="button" onClick={() => go('outbox')}>
                Go to the outbox
              </button>
            </p>
          </div>
        ) : null}

        {/*
          The jobs view is HIDDEN, never unmounted (D4). Unmounting `WorkFlow` on a tab
          switch destroyed the flow position and any half-filled form — so a technician
          checking the Outbox mid-job came back to the home screen with their answers gone.
          `hidden` removes the subtree from the accessibility tree and the tab order the
          same way it removes it from paint, so nothing here is reachable while hidden.
          The other two views stay conditionally mounted: they are cheap to rebuild and
          `OutboxPanel`'s per-row countdown timers should not run behind another view.
        */}
        <div hidden={view !== 'jobs'}>
          <WorkFlow
            onOpenOutbox={() => go('outbox')}
            onPathChange={(path) => {
              flowPath.current = path;
            }}
          />
        </div>
        {view === 'outbox' ? <OutboxPanel items={items} /> : null}
        {view === 'settings' ? <DevicePanel auth={auth} online={online} /> : null}
      </main>

      <nav className="app__nav" aria-label="Main">
        {VIEWS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => go(entry.id)}
            {...(view === entry.id ? { 'aria-current': 'page' as const } : {})}
            {...(entry.id === 'outbox' && pending > 0
              ? {
                  /*
                   * Without this the accessible name is the concatenation "Outbox 3" — a bare number
                   * with no unit, which could as easily be three failures as three successes. The label
                   * still STARTS with the visible word, so a voice-control user saying "tap Outbox"
                   * still matches it (WCAG 2.5.3). The accessible name keeps the REAL count — the
                   * "99+" cap below is a layout decision, not information a screen-reader user
                   * should lose.
                   */
                  'aria-label': `Outbox — ${pending} ${pending === 1 ? 'submission' : 'submissions'} waiting to send`,
                }
              : {})}
          >
            {entry.label}
            {entry.id === 'outbox' && pending > 0 ? (
              /* Capped so a pathological backlog cannot widen the nav button (and any backlog
                 that large is an outbox-screen problem, not a badge-precision problem). */
              <span className="badge" aria-hidden="true">
                {pending > 99 ? '99+' : pending}
              </span>
            ) : null}
          </button>
        ))}
      </nav>
    </div>
  );
}

/** Device and diagnostics. The one screen where the honest storage detail always shows. */
function DevicePanel({ auth, online }: { auth: AuthBinding; online: boolean }): JSX.Element {
  const storage = useStorageAssessment();
  const provider: TokenProvider = auth.provider;
  /**
   * The standing sign-in statement, in `phase.ts`'s words rather than in words invented here.
   *
   * This is the screen `SignInBanner`'s comment defers the standing state to, and it is the only place
   * two of the seven phases can be read at all: `signed-in` (the banner is deliberately quiet, so
   * without this there is no way to confirm a session exists) and `not-configured` (the banner region
   * gives that slot to `AuthStubBanner`, which names the real blocker). Both bodies carry the
   * pre-travel warning — *"a sign-in lasts about 10 hours and can only be renewed where there is
   * signal"* — which is worth reading on the screen a technician checks before setting off.
   */
  const notice = describeAuthPhase(auth.phase, online);
  return (
    <section>
      <h2 className="section-title">Storage</h2>
      {storage === null ? (
        <p className="muted">Checking…</p>
      ) : (
        /*
         * The verdict is rendered as a banner, not as a card with a chip in the corner.
         *
         * `assessStorage` returns FOUR outcomes and only one of them is good news, so this screen is
         * where the bad three have to be unmissable — this is the one place the detail always shows,
         * per the comment on `DevicePanel`. Previously the answer was a two-letter chip ("no") and the
         * sentence explaining what that costs the technician was `.muted`. The tone now follows
         * `durable`, and `storage.message` — which is the sentence written to be read by a technician
         * rather than an engineer — is body copy at full contrast.
         */
        <div
          className={`banner banner--${storage.durable ? 'ok' : 'danger'}`}
          role={storage.durable ? 'status' : 'alert'}
        >
          <p className="banner__title">
            {storage.durable
              ? 'Saved work is protected from automatic clearing'
              : 'Saved work is NOT protected from automatic clearing'}
          </p>
          <p className="banner__body">{storage.message}</p>
          {/*
            The raw four-way verdict is kept verbatim and in monospace. `not-persisted` (the browser was
            asked and refused) and `unsupported` (we could not ask) are the same colour of bad news to a
            technician but a different problem to whoever they report it to, so the word is not
            collapsed into the sentence above it.
          */}
          <p className="mono muted">
            verdict: {storage.persistence}
            {storage.usageBytes !== null ? ` · using ${formatBytes(storage.usageBytes)}` : ''}
            {storage.quotaBytes !== null ? ` of ~${formatBytes(storage.quotaBytes)}` : ''}
          </p>
          {storage.detail !== undefined ? <p className="mono muted">detail: {storage.detail}</p> : null}
        </div>
      )}

      <h2 className="section-title">Sign-in</h2>
      <article className="card">
        <div className="card__row">
          <strong>Token provider</strong>
          {/*
            `stub` was shown in the red `failed` chip. It is not a failure — it is an unbuilt feature,
            which is the exact distinction `AuthStubBanner` and `ApiNotice`'s `auth-not-configured`
            variant go out of their way to preserve. A red chip here undoes that work by teaching the
            technician that sign-in is broken and might therefore come back on its own.
          */}
          <span className={`state state--${provider.kind === 'stub' ? 'plain' : 'synced'}`}>
            {provider.kind === 'stub' ? 'not built' : provider.kind}
          </span>
        </div>
        <p className="muted">
          {provider.kind === 'stub'
            ? 'A stub. No token is issued and no request is sent to the API. This is not a signed-out state — it is an unbuilt feature.'
            : 'OIDC PKCE provider configured.'}
        </p>
      </article>

      {/*
        The phase, always, including the two the banner region never shows. Same shape as the storage
        verdict above it and the same reason: this is the screen where the detail does not hide, so the
        raw phase name goes next to the sentence rather than being collapsed into it — `session-stale`
        and `session-expired` read very differently to a technician and are a different problem to
        whoever they report it to.

        `data-auth-standing`, NOT `data-auth-phase`: the banner region above is still rendered on this
        view, so reusing `SignInBanner`'s attribute would put two elements carrying the same hook on one
        screen — and the first one a query found would depend on which phase happens to make the banner
        render at all.
      */}
      <div
        className={`banner banner--${notice.tone}`}
        role={notice.tone === 'danger' ? 'alert' : 'status'}
        data-auth-standing={auth.phase.kind}
      >
        <p className="banner__title">{notice.title}</p>
        <p className="banner__body">{notice.body}</p>
        <p className="mono muted">phase: {auth.phase.kind}</p>
        {notice.detail !== undefined ? <p className="mono muted">detail: {notice.detail}</p> : null}
      </div>

      <h2 className="section-title">Photos and scanning</h2>
      <article className="card">
        <p className="muted">
          Camera capture and barcode scanning are wired into the forms, where the schema asks for a
          photo or a serial — not on this screen. Photo bytes go to{' '}
          <span className="mono">core/blobs.ts</span>, and each upload is a separate outbox item created
          only after its submission has been accepted.
        </p>
        <p className="muted">
          The barcode decoder (~1 MiB of WASM) is fetched from this origin the first time a scan starts,
          so it is not part of the app's first download. Manual typing is always available and is not a
          fallback — it is the second primary path.
        </p>
      </article>

      {/*
        This caveat was an emoji at the front of a `.muted` paragraph at the bottom of a card — the
        quietest possible placement for the one sentence on the screen that limits how much anyone should
        trust the two features above it. The app already has an idiom for "notice this": use it. `status`
        rather than `alert`, because it is a standing limitation and not an event.
      */}
      <div className="banner banner--offline" role="status">
        <p className="banner__title">Unverified on real hardware</p>
        <p className="banner__body">
          Camera capture and barcode scanning have not been exercised on a real iPhone or Android handset
          in this change. If the camera misbehaves in the field, that is expected to be new — report it
          rather than working around it.
        </p>
      </div>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ['kB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}
